const computeAndCacheActiveStylesAndFormats = (nextState) => {
  nextState.activeStyleTypes = nextState.documentManager.getActiveStyleTypes();
  nextState.activeFormat = nextState.documentManager.getActiveFormat();
  nextState.activeHref = nextState.documentManager.getActiveHref();
};

export const getCursorStartPostion = ({ editableContentId }) => {
  const editableDocument = document.getElementById(editableContentId);

  if (document.activeElement !== editableDocument) {
    return;
  }

  const originalRange = window.getSelection().getRangeAt(0);

  const measuringRange = originalRange.cloneRange();
  measuringRange.selectNodeContents(editableDocument);
  measuringRange.setEnd(originalRange.startContainer, originalRange.startOffset);

  return measuringRange.toString().length;
};

export const getCursorEndPostion = ({ editableContentId }) => {
  const editableDocument = document.getElementById(editableContentId);

  if (document.activeElement !== editableDocument) {
    return;
  }

  const originalRange = window.getSelection().getRangeAt(0);

  const measuringRange = originalRange.cloneRange();
  measuringRange.selectNodeContents(editableDocument);
  measuringRange.setEnd(originalRange.endContainer, originalRange.endOffset);

  return measuringRange.toString().length;
};

export const getCurrentDocumentComponentAndCursorPosition = ({ node, targetPosition }) => {
  let pos = 0;
  let lastUnstyledContentStart = 0;
  let documentComponentNode = node;
  const unstyledContentNodeNames = [
    'H1',
    'H2',
    'H3',
    'P',
    'LI',
    'DIV',
  ];
  const stack = [node];

  while (stack.length > 0) {
      const current = stack.pop();

      if (current.nodeType === Node.TEXT_NODE) {
          const len = current.textContent.length;
          if (pos + len >= targetPosition) {
              return {
                documentComponentNode: documentComponentNode,
                documentComponentCursorPosition: targetPosition - lastUnstyledContentStart
              };
          }
          pos += len;
      } else if (current.childNodes && current.childNodes.length > 0) {
          if (unstyledContentNodeNames.includes(current.nodeName)) {
            lastUnstyledContentStart = pos;
            documentComponentNode = current;
          }

          for (let i = current.childNodes.length - 1; i >= 0; i--) {
              stack.push(current.childNodes[i]);
          }
      }
  };

  return {
    documentComponentNode: node,
    documentComponentCursorPosition: node.childNodes.length
  };
};

export const handleSelectionChange = ({ editableContentId, setState }) => {
  const cursorStartPosition = getCursorStartPostion({ editableContentId });
  const cursorEndPosition = getCursorEndPostion({ editableContentId });

  const editableDocument = document.getElementById(editableContentId);

  if (document.activeElement !== editableDocument) {
    return;
  }

  const {
    documentComponentNode: documentStartComponentNode,
    documentComponentCursorPosition: documentStartComponentCursorPosition
  } = getCurrentDocumentComponentAndCursorPosition({
    node: editableDocument,
    targetPosition: cursorStartPosition,
  });

  const {
    documentComponentNode: documentEndComponentNode,
    documentComponentCursorPosition: documentEndComponentCursorPosition
  } = getCurrentDocumentComponentAndCursorPosition({
    node: editableDocument,
    targetPosition: cursorEndPosition,
  });

  setState((prevState) => {
    const nextState = { ...prevState };
    nextState.documentManager.setCursorOnCurrentDocument({
      cursorStartPosition,
      cursorEndPosition,
      documentStartComponentCursorPosition,
      documentEndComponentCursorPosition,
      documentStartComponentNode,
      documentEndComponentNode,
    });

    computeAndCacheActiveStylesAndFormats(nextState);
    return nextState;
  });
};

export const createRangeFromStartAndEndNodesAndOffsets = ({
  startNode,
  startOffset,
  endNode,
  endOffset,
}) => {
  let range = document.createRange();
  range.setStart(startNode, startOffset);
  range.setEnd(endNode, endOffset);
  return range;
};

export const getDescendantNodeAndOffset = ({ node, targetPosition }) => {
  let pos = 0;
  const stack = [node];

  while (stack.length > 0) {
    const current = stack.pop();

    if (current.nodeType === Node.TEXT_NODE) {
      const len = current.textContent.length;
      if (pos + len >= targetPosition) {
        return {
          descendantNode: current,
          offset: targetPosition - pos,
        };
      }
      pos += len;
    } else if (current.childNodes && current.childNodes.length > 0) {
      for (let i = current.childNodes.length - 1; i >= 0; i--) {
        stack.push(current.childNodes[i]);
      }
    }
  }

  return {
    descendantNode: node,
    offset: node.childNodes.length,
  };
};

export const setCursorPositionInDOM = ({
  editableContentId,
  isibuteDocument,
}) => {
  const editableDocument = document.getElementById(editableContentId);

  if (!editableDocument) {
    return;
  }

  if (document.activeElement !== editableDocument) {
    return;
  }

  const {
    descendantNode: startNode,
    offset: startOffset
  } = getDescendantNodeAndOffset({
    node: editableDocument,
    targetPosition: isibuteDocument.cursorStartPosition
  });

  const {
    descendantNode: endNode,
    offset: endOffset
  } = getDescendantNodeAndOffset({
    node: editableDocument,
    targetPosition: isibuteDocument.cursorEndPosition
  });

  const range = createRangeFromStartAndEndNodesAndOffsets({
    startNode,
    startOffset,
    endNode,
    endOffset,
  });

  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);
};

export const updateFromKeydown = ({ event, setState }) => {
  const isPasteShortcut = (event.metaKey || event.ctrlKey) && event.key === 'v';
  if (isPasteShortcut) {
    return;
  }

  event.preventDefault();

  const persistedEvent = {
    key: event.key,
    altKeyIsPressed: event.altKey,
    ctrlKeyIsPressed: event.ctrlKey,
    metaKeyIsPressed: event.metaKey,
    shiftKeyIsPressed: event.shiftKey,
  };

  setState((prevState) => {
    const currentDoc = prevState.documentManager.getCurrentDocument();

    if (!currentDoc.currentStartNode || !currentDoc.currentEndNode) {
      return prevState;
    }

    if (currentDoc.currentStartNodeCursorPosition < 1 || currentDoc.currentEndNodeCursorPosition < 1) {
      return prevState;
    }

    const nextState = { ...prevState };
    nextState.documentManager.processEvent(persistedEvent);

    computeAndCacheActiveStylesAndFormats(nextState);
    return nextState;
  });
};

export const applyFormatToSelectedRange = ({ format, setState }) => {
  setState((prevState) => {
    const nextState = { ...prevState };

    try {
      nextState.documentManager.applyFormatToSelectedRange(format);
    } catch (e) {
      nextState.feedbackMessage = e.message;
      nextState.feedbackVariant = 'error';
    }

    computeAndCacheActiveStylesAndFormats(nextState);
    return nextState;
  });
};

export const applyStyleToSelectedRange = ({ style, setState }) => {
  setState((prevState) => {
    const nextState = { ...prevState };
    nextState.documentManager.applyStyleToSelectedRange(style);

    computeAndCacheActiveStylesAndFormats(nextState);
    return nextState;
  });
};

export const applyLinkToSelectedRange = ({ href, setState, editableContentId }) => {
  setState((prevState) => {
    const nextState = { ...prevState };
    nextState.documentManager.suppressSelectionChange = true;
    nextState.documentManager.applyLinkToSelectedRange(href);

    computeAndCacheActiveStylesAndFormats(nextState);
    return nextState;
  });

  if (editableContentId) {
    document.getElementById(editableContentId)?.focus();
  }
};

export const removeLinkFromSelectedRange = ({ setState, editableContentId }) => {
  setState((prevState) => {
    const nextState = { ...prevState };
    nextState.documentManager.suppressSelectionChange = true;
    nextState.documentManager.removeLinkFromSelectedRange();

    computeAndCacheActiveStylesAndFormats(nextState);
    return nextState;
  });

  if (editableContentId) {
    document.getElementById(editableContentId)?.focus();
  }
};

export const insertImageAtCursor = ({ pendingImageData, pendingImageFileName, caption, altText, displaySizeVariant, setState }) => {
  setState((prevState) => {
    const nextState = { ...prevState };

    try {
      nextState.documentManager.insertImage({
        pendingImageData,
        pendingImageFileName,
        caption,
        altText,
        displaySizeVariant,
      });
    } catch (e) {
      nextState.feedbackMessage = e.message;
      nextState.feedbackVariant = 'error';
    }

    return nextState;
  });
};

export const deleteImageComponent = ({ componentId, setState }) => {
  setState((prevState) => {
    const nextState = { ...prevState };

    try {
      nextState.documentManager.deleteImageComponent(componentId);
    } catch (e) {
      nextState.feedbackMessage = e.message;
      nextState.feedbackVariant = 'error';
    }

    return nextState;
  });
};

export const updateImageCaption = ({ componentId, caption, setState }) => {
  setState((prevState) => {
    const nextState = { ...prevState };

    try {
      nextState.documentManager.updateImageCaption({ componentId, caption });
    } catch (e) {
      nextState.feedbackMessage = e.message;
      nextState.feedbackVariant = 'error';
    }

    return nextState;
  });
};

export const insertParagraphAfterImage = ({ componentId, setState }) => {
  setState((prevState) => {
    const nextState = { ...prevState };

    try {
      nextState.documentManager.insertParagraphAfterImage({ componentId });
    } catch (e) {
      nextState.feedbackMessage = e.message;
      nextState.feedbackVariant = 'error';
    }

    return nextState;
  });
};

export const handlePaste = ({ event, setState }) => {
  event.preventDefault();

  const text = event.clipboardData?.getData('text/plain');
  if (!text) return;

  setState((prevState) => {
    const currentDoc = prevState.documentManager.getCurrentDocument();

    if (!currentDoc.currentStartNode || !currentDoc.currentEndNode) {
      return prevState;
    }

    if (currentDoc.currentStartNodeCursorPosition < 1 || currentDoc.currentEndNodeCursorPosition < 1) {
      return prevState;
    }

    const nextState = { ...prevState };
    nextState.documentManager.pasteText(text);

    computeAndCacheActiveStylesAndFormats(nextState);
    return nextState;
  });
};
